import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_URL as string;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const maxRetries = 2;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const headers: Record<string, string> = {
      ...(await getAuthHeaders()),
    };

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 429 && attempt < maxRetries) {
      const retryAfter = res.headers.get('Retry-After');
      const delay = retryAfter ? Math.min(Number(retryAfter) * 1000, 5000) : (attempt + 1) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
      continue;
    }

    if (res.status >= 500 && attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, (attempt + 1) * 1000));
      continue;
    }

    if (res.status === 401) {
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      const errorMsg = ((data.error as string) ?? '').toLowerCase();
      if (
        (errorMsg.includes('token') || errorMsg.includes('expired')) &&
        !window.location.pathname.startsWith('/callback')
      ) {
        await supabase.auth.signOut();
        window.location.href = '/sign-in';
      }
      throw new ApiError(401, (data.error as string) ?? 'Unauthorized');
    }

    if (res.status === 429) {
      throw new ApiError(429, 'Too many requests. Please wait a moment and try again.');
    }

    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError(
        res.status,
        (data.error as string | undefined) ??
          (data.message as string | undefined) ??
          `Request failed (${res.status})`,
      );
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  throw new ApiError(500, 'Request failed after retries');
}

export function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export function del(path: string): Promise<void> {
  return request<void>('DELETE', path);
}
